import React from "react";
import { useSearchParams } from "react-router-dom";

import type { AccessRequestFilterShape } from "@/@types/access-request.types";
import { Meta } from "@/@types/paginated";
import { AccessTabState } from "@/components/access-request/access-tab-state";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ACCESS_REQUEST_BASE_PATH,
  ACCESS_REQUEST_TAB,
  ACCESS_REQUEST_TAB_PARAM,
  ACCESS_REQUEST_TABS,
  AccessRequestTab,
} from "@/constants/access-request";
import { UNLOCK_TAB_LABELS } from "@/constants/messages";
import UnlockTab from "@/components/unlock-request/unlock-tab";
import { AccessRequestDTO } from "@/services/dtos/accessRequest.dto";
import { useGetAccessRequestQuery } from "@/services/request-access";
import { applyToSearchParams, readOneOf } from "@/utils/search-params";

import { ContentContainer, PageLayout, PageTitle } from "components/layout";

import { columns } from "../components/columns";
import { DataTable } from "../components/data-table";
import { useFilterState } from "../ducks/filter/hooks/filterState";

export const AccessRequest: React.FC = () => {
  const filterState: AccessRequestFilterShape =
    useFilterState() as AccessRequestFilterShape;
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab: AccessRequestTab =
    readOneOf(searchParams.get(ACCESS_REQUEST_TAB_PARAM), ACCESS_REQUEST_TABS) ??
    ACCESS_REQUEST_TAB.ACCESS;

  const filters: AccessRequestFilterShape["filters"] = filterState?.filters;

  const {
    data: accessRequest,
    isLoading,
    isFetching,
    error,
  } = useGetAccessRequestQuery(
    {
      page: filters?.meta?.page ?? 1,
      perPage: filters?.meta?.perPage ?? 10,
      search: filters?.search || "",
      store_code: filters?.store_code || "",
      from_date: filters?.from_date || "",
      to_date: filters?.to_date || "",
      status: filters?.status || "",
      orderBy: filters?.orderBy ?? {},
    },
    {
      refetchOnMountOrArgChange: true,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    },
  );

  const rows: AccessRequestDTO[] = accessRequest?.data ?? [];
  const meta: Partial<Meta> = accessRequest?.meta ?? {};

  const changeTab = (value: string): void => {
    setSearchParams(
      applyToSearchParams(searchParams, { [ACCESS_REQUEST_TAB_PARAM]: value }),
      { replace: true },
    );
  };

  return (
    <PageLayout>
      <PageTitle
        title="Requisição de acesso"
        description="Gerencie as solicitações de acesso e de desbloqueio das lojas"
      />
      <ContentContainer>
        <Tabs value={activeTab} onValueChange={changeTab} className="w-full">
          <TabsList className="border-b border-border w-full justify-start rounded-none h-auto">
            <TabsTrigger value={ACCESS_REQUEST_TAB.ACCESS}>
              {UNLOCK_TAB_LABELS.TAB_ACCESS}
            </TabsTrigger>
            <TabsTrigger value={ACCESS_REQUEST_TAB.UNLOCK}>
              {UNLOCK_TAB_LABELS.TAB_UNLOCK}
            </TabsTrigger>
          </TabsList>

          <TabsContent value={ACCESS_REQUEST_TAB.ACCESS} className="mt-4">
            <AccessTabState isLoading={isLoading} hasError={!!error}>
              <DataTable
                data={rows}
                columns={columns}
                meta={meta}
                isLoading={isFetching}
              />
            </AccessTabState>
          </TabsContent>

          <TabsContent value={ACCESS_REQUEST_TAB.UNLOCK} className="mt-4">
            <UnlockTab detailPathPrefix={ACCESS_REQUEST_BASE_PATH.FINANCE} />
          </TabsContent>
        </Tabs>
      </ContentContainer>
    </PageLayout>
  );
};
